import { initChatWidget, CHAT_CONFIG } from './chatInit.js'
import AuthService from '@/features/auth/authService.js'

export async function openAdminChat(initialMessage = null) {
  try {
    await initChatWidget()

    const rider = AuthService.getUserData()
    const riderId = rider?.id || rider?._id
    const riderName = rider
      ? `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || rider.name || rider.email || 'Livreur'
      : 'Livreur'

    const adminId = CHAT_CONFIG.adminUserId || 'admin-1'

    const conv = await window.XenoTalk.openConversation({
      // Participant 1 : Le Livreur
      participant1Id: riderId,
      participant1Role: 'rider',
      participant1Name: riderName,
      participant1Tag: 'Livreur',
      participant1NotifService: 'rapidoo-rider',

      // Participant 2 : L'Administrateur
      participant2Id: adminId,
      participant2Role: 'admin',
      participant2Name: 'Support Client',
      participant2Tag: 'Support Client',
      participant2NotifService: 'rapidoo',

      // Types et Référence
      type: 'support',
      referenceType: 'admin',
      referenceId: `v3-admin-rider-${riderId}`
    })

    await window.XenoTalk.setConversation(conv._id, {
      title: 'Support Client',
      tag: 'Support Client',
      conversation: conv
    })
    
    if (initialMessage && typeof window.XenoTalk.sendMessage === 'function') {
      window.XenoTalk.sendMessage(initialMessage)
    }
    
    window.XenoTalk.openUI()
  } catch (error) {
    console.error('Erreur lors de l\'ouverture du chat avec le support:', error)
  }
}

export async function openCustomerChat(order) {
  try {
    if (!order?.id) return
    await initChatWidget()

    const rider = AuthService.getUserData()
    const riderId = rider?.id || rider?._id
    const riderName = rider
      ? `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || rider.name || rider.email || 'Livreur'
      : 'Livreur'
      
    const isPackage = order.type === 'package'
    const itemType = isPackage ? 'package' : 'order'
    const referenceId = `v3-rider-customer-${order.id}`

    const customerId = order.customerId || order.userId || order.user?.id || order.senderId
    const customerName = order.customerName || order.recipientName || order.senderName || 'Client'

    const conv = await window.XenoTalk.openConversation({
      participant1Id: riderId,
      participant1Role: 'rider',
      participant1Name: riderName,
      participant1Tag: 'Livreur',
      participant1NotifService: 'rapidoo-rider',

      participant2Id: customerId,
      participant2Role: 'client',
      participant2Name: customerName,
      participant2Tag: 'Client',
      participant2NotifService: 'rapidoo',

      type: 'delivery',
      referenceType: itemType,
      referenceId: referenceId,

      metadata: {
        type: itemType,
        orderId: isPackage ? null : order.id,
        packageId: isPackage ? order.id : null
      }
    })

    await window.XenoTalk.setConversation(conv._id, {
      title: customerName,
      tag: 'Client',
      conversation: conv
    })
    window.XenoTalk.openUI()
  } catch (error) {
    console.error('Erreur lors de l\'ouverture du chat client:', error)
  }
}

export async function sendCustomerMessage(order, message = null) {
  try {
    if (!order?.id) return
    await initChatWidget()

    const rider = AuthService.getUserData()
    const riderId = rider?.id || rider?._id
    const riderName = rider
      ? `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || rider.name || rider.email || 'Livreur'
      : 'Livreur'
      
    const customerId = order.customerId || order.userId || order.user?.id || order.senderId
    const customerName = order.customerName || order.recipientName || order.senderName || 'Client'
    const isPackage = order.type === 'package'
    const itemType = isPackage ? 'package' : 'order'
    const referenceId = `v3-rider-customer-${order.id}`

    if (!customerId) {
      console.warn('[sendCustomerMessage] customerId missing:', { customerId })
      return
    }

    const conv = await window.XenoTalk.openConversation({
      participant1Id: riderId,
      participant1Role: 'rider',
      participant1Name: riderName,
      participant1Tag: 'Livreur',
      participant1NotifService: 'rapidoo-rider',

      participant2Id: customerId,
      participant2Role: 'client',
      participant2Name: customerName,
      participant2Tag: 'Client',
      participant2NotifService: 'rapidoo',

      type: 'delivery',
      referenceType: itemType,
      referenceId: referenceId,

      metadata: {
        type: itemType,
        orderId: isPackage ? null : order.id,
        packageId: isPackage ? order.id : null
      }
    })

    const messageText = message || `Bonjour, je suis ${riderName}, je viens d'accepter votre course et je démarre déjà.`

    if (conv?._id && typeof window.XenoTalk.sendMessage === 'function') {
      await window.XenoTalk.setConversation(conv._id, {
        title: customerName,
        tag: 'Client',
        conversation: conv
      })
      await window.XenoTalk.sendMessage(messageText)
      console.log('[sendCustomerMessage] Message sent successfully to customer:', customerId)
    }
  } catch (error) {
    console.error('Erreur lors de l\'envoi direct du message client:', error)
  }
}

export function resetChat() {
  if (window.XenoTalk && typeof window.XenoTalk.closeUI === 'function') {
    window.XenoTalk.closeUI()
  }
}

/**
 * Récupère le nombre de messages non lus pour une commande / colis spécifique
 * @param {string|Object} orderOrId 
 * @returns {number}
 */
export function getUnreadCountForOrder(orderOrId) {
  if (!orderOrId || typeof window === 'undefined' || !window.XenoTalk) return 0
  const id = typeof orderOrId === 'object' ? (orderOrId.id || orderOrId._id) : orderOrId
  if (!id) return 0
  return typeof window.XenoTalk.getConversationUnreadCount === 'function'
    ? window.XenoTalk.getConversationUnreadCount(`v3-rider-customer-${id}`)
    : 0
}

/**
 * Récupère le nombre total de conversations non lues
 * @returns {number}
 */
export function getTotalUnreadCount() {
  if (typeof window === 'undefined' || !window.XenoTalk) return 0
  return typeof window.XenoTalk.getTotalUnreadCount === 'function'
    ? window.XenoTalk.getTotalUnreadCount()
    : 0
}

/**
 * S'abonne aux changements des messages non lus du chat
 * @param {Function} callback 
 * @returns {Function} Fonction de désabonnement
 */
export function onChatUnreadChange(callback) {
  if (typeof window === 'undefined') return () => {}
  const handler = (e) => {
    if (typeof callback === 'function') {
      callback(e.detail || e)
    }
  }
  window.addEventListener('xenotalk:unread_change', handler)
  window.addEventListener('xenotalk:conversations_updated', handler)
  return () => {
    window.removeEventListener('xenotalk:unread_change', handler)
    window.removeEventListener('xenotalk:conversations_updated', handler)
  }
}